import type { PrimitiveValue, TemporalValue } from "@/common";
import { createAppResourceService, getCurrentUser, global } from "@/global";
import { useAuth } from "@/legacy/redux";
import type { PubSubMessage, Resource, createResourceService } from "@imminently/immi-query";
import { filterAction } from "@imminently/immi-query";
// import { filter } from "rxjs/operators";
import type {
  Document,
  FileAttachment,
  FileProcessingFileInfo,
  FileProcessingTestLookupResponse,
  FileProcessingTestResourceDetail,
  FileProcessingTestResourceReference,
  FileProcessingTestRun,
  FileTokenResponse,
  Job,
  Project,
  Release,
  StartFileProcessingTestInput,
  StartFileProcessingTestResponse,
  Task,
  Workspace,
} from "@packages/commons";
import { useEffect, useState } from "react";
import { BehaviorSubject, timer } from "rxjs";

export type Service = ReturnType<typeof createResourceService>;

export interface Interaction {
  id: string;
  attachments?: FileAttachment[];

  [key: string]: any;
}

export interface EventIndexHealResponse {
  day: string;
  limit: number;
  skip: number;
  nextSkip: number | null;
  scanned: number;
  counts: Record<string, number>;
  sample: Array<{
    id?: string;
    status: string;
    eventType?: string;
    tenancyId?: string;
    modelId?: string;
    sessionId?: string;
    indexValues: string[];
    message?: string;
  }>;
}

export interface FileProcessingEvent {
  id?: string;
  eventRowKey?: string;
  eventType?: string;
  status?: string;
  timestamp?: string;
  modelId?: string;
  meta?: Record<string, any>;
  facts?: { key: string; value: unknown }[];
  [key: string]: any;
}

export type InvestigationCardType =
  | "attribute-list"
  | "text"
  | "timesheet-table"
  | "attribute-explanation"
  | "rule-graph"
  | "html";

export interface InvestigationCardLayout {
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  maxW?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

export interface InvestigationCardConfig {
  [key: string]: any;
}

export interface InvestigationCardDefinition {
  id: string;
  type: InvestigationCardType;
  title?: string;
  layout: InvestigationCardLayout;
  config: InvestigationCardConfig;
}

export interface InvestigationTemplate {
  schemaVersion: number;
  id: string;
  modelId: string;
  name: string;
  canvas: {
    width?: number;
    zoom?: number;
    cards: InvestigationCardDefinition[];
  };
}

export interface InvestigationMetadata {
  id: string;
  name: string;
  description?: string;
  isDefault?: boolean;
  created?: string;
  createdBy?: string;
  lastModified?: string;
  lastModifiedBy?: string;
}

export interface InvestigationRecord extends InvestigationMetadata {
  template: InvestigationTemplate;
}

/** One page in an investigation export: project-bound ids are stripped, HTML card content is included. */
export interface ExportedInvestigation {
  name: string;
  description?: string;
  isDefault?: boolean;
  template: Omit<InvestigationTemplate, "id" | "modelId" | "name">;
  /** Saved project-level state of each HTML card, keyed by card id. Session state is never exported. */
  cardState: Record<string, unknown>;
}

export interface InvestigationExport {
  format: "decisively-investigations";
  formatVersion: 1;
  exportedAt: string;
  source: { modelId: string; modelName?: string };
  investigations: ExportedInvestigation[];
}

export interface InvestigationImportResult {
  investigations: { id: string; name: string; isDefault: boolean }[];
}

// Decision API endpoint definitions
export interface SessionResponse {
  id: string;
  graph?: any;
  sessionId?: string;
  attachments?: FileAttachment[];
  interactions?: Interaction[];
  lastInteractionId?: string;

  [key: string]: any;
}

export type AuthUser = Resource & {
  identity_id: string;
  first_name: string;
  last_name: string;
  email: string;
  roles: string[];
  rolesError?: boolean;
};

/** Re-export of useAuth for ease of use
 *
 * @returns the user stored in redux
 */
export const useUser = () => {
  const { user } = useAuth();
  return user as AuthUser;
};

export type AuthKey = Resource & {
  name: string;
  tenancy: string;
  validUntil: number | string;
  roles?: string[];
};

export type HelpArticle = Resource & {
  name: string;
  content: string;
};

export type AuthTenancy = Resource & {
  name: string;
  description: string;
};

export type FlowTask = Omit<Task, "model"> & {
  name: string;
  outcome: any;
  justification: string;
  approvalLabel?: string;
  approvalOutcome?: string;
  rejectionLabel?: string;
  rejectionOutcome?: string;
  model: string;
  interview: string;
  interaction: string;
  session: string;
};

export interface Contact extends Resource {
  // id: string;
  created: Date;
  lastModified: Date;
  firstName?: TemporalValue | PrimitiveValue;
  lastName?: TemporalValue | PrimitiveValue;
  /** The contact's identity provider (sign-in) user. Refreshed from the identity provider when the contact is fetched. */
  identityId?: string | null;
  /** Whether the identity provider user was created for the contact or linked by email. */
  identitySource?: "created" | "linked" | null;
}

export type OrgModelType =
  | "parent"
  | "approver"
  | "member"
  | "delegate"
  | "resolvedApprover"
  | "hierarchySummary"
  | "hierarchyIssue";

export interface ContactSummary extends Resource {
  firstName?: PrimitiveValue;
  lastName?: PrimitiveValue;
  email?: PrimitiveValue;
}

export interface OrgModelRecord extends Resource {
  type: OrgModelType;
  subjectType: string;
  subjectId: string;
  relatedId?: string;
  level?: number;
  parentCount?: number;
  childCount?: number;
  childrenCount?: number;
  parentIds?: string[];
  childIds?: string[];
  ancestorIds?: string[];
  ancestors?: Array<{ subjectId: string; level: number }>;
  depth?: number;
  issueType?: "cycle";
  cyclePath?: string[];
  message?: string;
  validFrom?: string;
  validTo?: string;
  source?: "direct" | "computed" | "override";
  subjectContact?: ContactSummary;
  relatedContact?: ContactSummary;
}

export interface Extension extends Resource {
  type?: string;
  contact?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  startDateTime?: string;
  endDateTime?: string;
  created?: string;
  lastModified?: string;
  timesheets?: Array<Record<string, unknown>>;
  history?: Array<Record<string, unknown>>;

  [key: string]: unknown;
}

export const workspaceService = createAppResourceService<Workspace>("workspaces");
export const projectService = createAppResourceService<Project>("models");

export const listInvestigations = projectService
  .endpoint()
  .paramsOf<"id">()
  .responseOf<InvestigationMetadata[]>()
  .build({
    id: "listInvestigations",
    path: "/:id/investigations",
    method: "get",
  });

export const getInvestigation = projectService
  .endpoint()
  .paramsOf<"id" | "investigationId">()
  .responseOf<InvestigationRecord>()
  .build({
    id: "getInvestigation",
    path: "/:id/investigations/:investigationId",
    method: "get",
  });

export const createInvestigation = projectService.endpoint().paramsOf<"id">().responseOf<InvestigationRecord>().build({
  id: "createInvestigation",
  path: "/:id/investigations",
  method: "post",
});

export const updateInvestigation = projectService
  .endpoint()
  .paramsOf<"id" | "investigationId">()
  .responseOf<InvestigationRecord>()
  .build({
    id: "updateInvestigation",
    path: "/:id/investigations/:investigationId",
    method: "patch",
  });

export const deleteInvestigation = projectService
  .endpoint()
  .paramsOf<"id" | "investigationId">()
  .responseOf<{ id: string; deleted: boolean }>()
  .build({
    id: "deleteInvestigation",
    path: "/:id/investigations/:investigationId",
    method: "delete",
  });

/** Export one investigation page, or every page when `investigationId` is omitted. */
export const exportInvestigations = projectService
  .endpoint()
  .paramsOf<"id">()
  .queryOf<{ investigationId?: string }>()
  .responseOf<InvestigationExport>()
  .build({
    id: "exportInvestigations",
    path: "/:id/custom/exportInvestigations",
    method: "get",
  });

/** Import pages from an investigation export into this project. */
export const importInvestigations = projectService
  .endpoint()
  .paramsOf<"id">()
  .bodyOf<{ payload: InvestigationExport }>()
  .responseOf<InvestigationImportResult>()
  .build({
    id: "importInvestigations",
    path: "/:id/custom/importInvestigations",
    method: "post",
  });

/** Get a short-lived upload token for file processing scoped staging. GET /models/:id/custom/getFileToken */
export const getFileToken = projectService.endpoint().paramsOf<"id">().responseOf<FileTokenResponse>().build({
  id: "getFileToken",
  path: "/:id/custom/getFileToken",
  method: "get",
});

/** Upload a CSV to scoped staging and start a real file-processing test. */
export const startFileProcessingTest = projectService
  .endpoint()
  .bodyOf<StartFileProcessingTestInput>()
  .paramsOf<"id">()
  .responseOf<StartFileProcessingTestResponse>()
  .build({
    id: "startFileProcessingTest",
    path: "/:id/custom/startFileProcessingTest",
    method: "post",
  });

/** Reload a retained file-processing test snapshot. */
export const getFileProcessingTest = projectService
  .endpoint()
  .paramsOf<"id">()
  .responseOf<FileProcessingTestLookupResponse>()
  .build({
    id: "getFileProcessingTest",
    path: "/:id/custom/getFileProcessingTest",
    method: "get",
  });

/** Load the full JSON for one resource sampled by a retained file-processing test. */
export const getFileProcessingTestResource = projectService
  .endpoint()
  .bodyOf<{ runId: string; scope: FileProcessingTestResourceReference["scope"]; resourceId: string }>()
  .paramsOf<"id">()
  .responseOf<FileProcessingTestResourceDetail>()
  .build({
    id: "getFileProcessingTestResource",
    path: "/:id/custom/getFileProcessingTestResource",
    method: "post",
  });

/** Remove a retained file-processing test snapshot before its normal expiry. */
export const clearFileProcessingTest = projectService
  .endpoint()
  .bodyOf<{ runId: string }>()
  .paramsOf<"id">()
  .responseOf<{ id: string; deleted: boolean }>()
  .build({
    id: "clearFileProcessingTest",
    path: "/:id/custom/clearFileProcessingTest",
    method: "post",
  });

/** List files in scoped staging, processed, or error folders. GET /models/:id/files?type=&scope= */
export const listModelFiles = projectService.endpoint().paramsOf<"id">().responseOf<FileProcessingFileInfo[]>().build({
  id: "listModelFiles",
  path: "/:id/files",
  method: "get",
});

/** Get file content by ID. GET /models/:id/files/:fileId */
export const getModelFile = projectService
  .endpoint()
  .paramsOf<"id" | "fileId">()
  .responseOf<{ name: string; data: string }>()
  .build({
    id: "getModelFile",
    path: "/:id/files/:fileId",
    method: "get",
  });

export interface ReprocessModelFileResponse {
  id: string;
  jobId: string;
  mode: "file" | "failedRows";
  scope: string;
  /** The staged file's name: the file's own name, or a `.retry-<time>.csv` file for failed rows. */
  fileName: string;
  path: string;
  sourcePath: string;
}

/** Send a file in an error folder through processing again. POST /models/:id/custom/reprocessFile */
export const reprocessModelFile = projectService
  .endpoint()
  .bodyOf<{ fileId: string }>()
  .paramsOf<"id">()
  .responseOf<ReprocessModelFileResponse>()
  .build({
    id: "reprocessModelFile",
    path: "/:id/custom/reprocessFile",
    method: "post",
  });

/** List file processing events for a model. GET /models/:id/events */
export const listModelFileProcessingEvents = projectService
  .endpoint()
  .paramsOf<"id">()
  .responseOf<{ data: FileProcessingEvent[]; nextCursor?: string }>()
  .build({
    id: "listModelFileProcessingEvents",
    path: "/:id/events",
    method: "get",
  });

export const releaseService = createAppResourceService<Release>("releases");
export const releaseJobsService = createAppResourceService<
  Job & { release?: Pick<Release, "id" | "releaseNo" | "env" | "description" | "model"> }
>("releases/jobs");
export const scenarioService = createAppResourceService<any>("scenarios");
export const scenarioRunService = createAppResourceService<any>("scenarioruns");
export const tasksService = createAppResourceService<Task>("tasks");
export const flowTasksService = createAppResourceService<FlowTask>("tasks");
export const reportService = createAppResourceService<any>("decisionreports");
export const userService = createAppResourceService<AuthUser>("auth/users");
export const keyService = createAppResourceService<AuthKey>("auth/keys");
export const helpService = createAppResourceService<HelpArticle>("auth/help");
export const tenancyService = createAppResourceService<AuthTenancy>("auth/tenancies");

export const sessionService = createAppResourceService<SessionResponse>("sessions");

export const healEventIndexes = createAppResourceService<any>("auth")
  .endpoint()
  .bodyOf<{ day: string; limit?: number; skip?: number; dryRun?: boolean; status?: string }>()
  .responseOf<EventIndexHealResponse>()
  .build({
    id: "healEventIndexes",
    path: "/events/heal-indexes",
    method: "post",
  });

/**
 * Encode attributeId (may contain "/") for use in URL path so routing does not split on slashes.
 * Two-way: server decodes with decodeAttributeIdFromPath to recover the original path.
 * Uses base64url (base64 with +/ replaced by -_) so the path segment has no slashes.
 */
export function encodeAttributeIdForPath(attributeId: string): string {
  const base64 = btoa(unescape(encodeURIComponent(attributeId)));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export interface SessionChangeTimelineEntry {
  when: number;
  interactionId: string;
  journeyId?: string;
  whoSummary?: string;
  /** Full who object from blob (name, roles, identity_id, etc.) */
  who?: unknown;
  oldValue?: unknown;
  newValue?: unknown;
}

export interface SessionFileResponse {
  name: string;
  container?: string;
  contentType?: string;
  encoding?: "base64" | string;
  data: string;
}

const getSessionHistoryEndpoint = sessionService
  .endpoint()
  .paramsOf<"sessionId" | "attributeId">()
  .responseOf<SessionChangeTimelineEntry[]>()
  .build({
    id: "get_session_history",
    path: "/:sessionId/history/:attributeId",
    method: "get",
  });

/** Get the change history for a specific attribute in a session. */
export async function getSessionHistory(sessionId: string, attributeId: string): Promise<SessionChangeTimelineEntry[]> {
  const encodedAttributeId = encodeAttributeIdForPath(attributeId);
  const response = await getSessionHistoryEndpoint.submit({
    params: { sessionId, attributeId: encodedAttributeId },
  });
  return response.data;
}

/** One derived table instance's persisted rows - see DerivedTableInstanceRows in decisionapi. */
export interface StoredDerivedTableInstanceRows {
  entityInstance: string | null;
  rows: Record<string, unknown>[];
}

export interface StoredDerivedTableResult {
  id: string;
  tables: StoredDerivedTableInstanceRows[];
}

const getSessionDerivedTableEndpoint = sessionService
  .endpoint()
  .paramsOf<"sessionId" | "derivedTableId">()
  .responseOf<StoredDerivedTableResult>()
  .build({
    id: "get_session_derived_table",
    path: "/:sessionId/derivedTable/:derivedTableId",
    method: "get",
  });

/**
 * Get the derived table rows persisted for this session version (computed once at solve time,
 * not recomputed here) - grouped by entity instance, empty when this table was never referenced
 * by a delta mapping when the session was solved.
 */
export async function getSessionDerivedTable(
  sessionId: string,
  derivedTableId: string,
): Promise<StoredDerivedTableResult> {
  const response = await getSessionDerivedTableEndpoint.submit({
    params: { sessionId, derivedTableId },
  });
  return response.data;
}

export interface RefireSessionResult {
  refired: number;
  interactionIds: string[];
}

/** Refire failed outbound interaction(s). Pass interaction id or "all" to refire all failed. */
export async function refireSession(
  sessionId: string,
  interactionId?: string | "all",
  sessionVersion?: number | string,
): Promise<RefireSessionResult> {
  const customService = createAppResourceService<any>(`sessions/${sessionId}/custom`);
  const interactionParam = interactionId && interactionId !== "all" ? interactionId : "all";
  const payload: Record<string, any> = { interaction: interactionParam };
  if (sessionVersion !== undefined && sessionVersion !== null && String(sessionVersion).trim() !== "") {
    payload.sessionVersion = sessionVersion;
  }
  const response = await customService.update({
    id: "refire",
    payload,
  });
  return response.data;
}

/** Run ad-hoc session mapping (response or delta). Returns the mapped output. For delta with includeDebug, returns { output, currentRecords, previousRecords }. */
export async function getSessionMappingOutput(
  sessionId: string,
  mappingId: string,
  options?: { filter?: string; includeDebug?: boolean },
): Promise<any> {
  const queryParams: Record<string, string> = {};
  if (options?.filter?.trim()) {
    queryParams.filter = options.filter.trim();
  }
  if (options?.includeDebug === true) {
    queryParams.includeDebug = "true";
  }
  const path = `sessions/${sessionId}/mapping/${mappingId}`;
  const url = Object.keys(queryParams).length > 0 ? `${path}?${new URLSearchParams(queryParams).toString()}` : path;
  const response = await global.client.get(url);
  return response.data !== undefined ? response.data : response;
}

/** POST /sessions/:sessionId/mapping/adhoc?release= — ad-hoc mapping (e.g. portal response CSV test). Auth via global client. */
export async function postSessionMappingAdhoc(
  sessionId: string,
  releaseId: string,
  body: {
    type: string;
    mapping: Record<string, unknown>;
    data?: Record<string, unknown>;
    previousData?: Record<string, unknown>;
    useSessionData?: boolean;
  },
  options?: { includeDebug?: boolean },
): Promise<{
  result?: unknown;
  mapped?: unknown;
  output?: unknown;
  currentRecords?: unknown[];
  previousRecords?: unknown[];
  data?: unknown;
  previousData?: unknown;
  message?: string;
}> {
  const params = new URLSearchParams({
    release: String(releaseId),
    ...(options?.includeDebug ? { includeDebug: "true" } : {}),
  });
  const path = `sessions/${encodeURIComponent(String(sessionId))}/mapping/adhoc?${params.toString()}`;
  const response = await global.client.post(path, body);
  return response.data !== undefined ? response.data : response;
}

/** Get outbound storage file output for a session interaction id. */
export async function getSessionFileOutput(sessionId: string, interactionId: string): Promise<SessionFileResponse> {
  const path = `sessions/${sessionId}/files/${interactionId}`;
  const response = await global.client.get(path);
  return response.data !== undefined ? response.data : response;
}

export const fileUploadService = createAppResourceService<any>("decisionapi/file");
export const decisionapiService = createAppResourceService<any>("decisionapi");
export const mappingContactsService = createAppResourceService<any>("auth/meta/mappings");

// Variables bulk replace endpoint
export interface Variable {
  id: string;
  key: string;
  value: string;
  created?: string;
  lastModified?: string;
}

export const createReleaseVariable = releaseService
  .endpoint()
  .bodyOf<Variable>()
  .paramsOf<"releaseId">()
  .responseOf<Variable>()
  .build({
    id: "create_release_variable",
    path: "/:releaseId/variables",
    method: "post",
  });

export const updateReleaseVariable = releaseService
  .endpoint()
  .bodyOf<Partial<Variable>>()
  .paramsOf<"releaseId" | "variableId">()
  .responseOf<Variable>()
  .build({
    id: "update_release_variable",
    path: "/:releaseId/variables/:variableId",
    method: "patch",
  });

export const deleteReleaseVariable = releaseService
  .endpoint()
  .paramsOf<"releaseId" | "variableId">()
  .responseOf<Variable>()
  .build({
    id: "delete_release_variable",
    path: "/:releaseId/variables/:variableId",
    method: "delete",
  });

export const patchSession = decisionapiService
  .endpoint()
  .bodyOf<{
    data: Record<string, any>;
    response: Array<{ type: string; debug?: boolean }>;
  }>()
  .paramsOf<"model" | "release">()
  .responseOf<SessionResponse>()
  .build({
    id: "patch_session",
    path: "/session/:model/:release",
    method: "patch",
  });

export const postSession = decisionapiService
  .endpoint()
  .bodyOf<{
    data: Record<string, any>;
    response: Array<{ type: string; debug?: boolean }>;
  }>()
  .paramsOf<"model" | "release">()
  .responseOf<SessionResponse>()
  .build({
    id: "post_session",
    path: "/session/:model/:release",
    method: "post",
  });

// export type ContactServiceItemT = { id: string } & Record<string, unknown>;
export const contactsService = createAppResourceService<Contact>("contacts");

export interface ContactIdentifierRecord {
  id: string;
  namespace: string;
  key: string;
  value: string;
  contactId: string;
  created?: string;
  lastModified?: string;
}

export interface ContactIdentifierListResponse {
  items: ContactIdentifierRecord[];
  total: number;
}

export const listContactIdentifiers = contactsService
  .endpoint()
  .queryOf<{
    $limit: number;
    $skip: number;
    $orderBy: string;
  }>()
  .responseOf<{ data: ContactIdentifierRecord[]; total: number }>()
  .build({
    id: "listContactIdentifiers",
    path: "/custom/listContactIdentifiers",
    method: "get",
  });

export const purgeContactIdentifiers = contactsService
  .endpoint()
  .responseOf<{ deletedCount: number }>()
  .build({
    id: "purgeContactIdentifiers",
    path: "/custom/purgeContactIdentifiers",
    method: "delete",
  });

export const orgModelsService = createAppResourceService<OrgModelRecord>("orgmodels");
export const extensionsService = createAppResourceService<Extension>("entities");

export interface OrgModelsRebuildDerivedResponse {
  queued: boolean;
  jobId: string;
  tenancy?: string;
  options: {
    implyApprovers: boolean;
  };
}

export async function rebuildOrgModelsDerived(): Promise<OrgModelsRebuildDerivedResponse> {
  const response = await global.client.post("orgmodels/custom/rebuildDerived", {});
  return response.data !== undefined ? response.data : response;
}

// TODO the pubsub for this is trying to fetch the token before we have valid auth
// need a method to ensure auth exists before we query
// ideally we want this to work inside the library to avoid this being a common problem
export const documentService = createAppResourceService<Document>("documents");

type Reader = { id: string; resource: string; identity_id: string; full_name: string; time: number };
// type DocumentMsg = PubSubMessage<Document>;
type ReaderMsg = PubSubMessage<Reader>;

// export const useDocumentTest = () => {
//   useEffect(() => {
//     // the encounter service pubsub can also emit other data types
//     const sub = documentService.pubsub$
//       // .pipe<DocumentMsg>(filterAction("nextobs"))
//       .subscribe((msg) => {
//         console.log("[Document Pubsub] received message", msg);
//       });
//     return () => {
//       console.log("[Document Pubsub] unsubscribing...");
//       sub.unsubscribe();
//     };
//   }, []);

//   return { };
// };

// if we use an app wide store, it's still not 100% accurate but would be better than nothing
// using rxjs as a store

type EditorMap = Map<string, Reader[]>;
const _documentEditors = new Map<string, Reader[]>();
const $editors = new BehaviorSubject<EditorMap>(_documentEditors);

// 30 second timeout
timer(0, 30000).subscribe(() => {
  // console.log("[Document Pubsub] clearing editors");
  const user = getCurrentUser();
  const isSelf = (e: Reader) => e.identity_id === user?.profile.oid;
  // _documentEditors.clear();
  _documentEditors.forEach((v, k) => {
    const editors = v.filter((e) => isSelf(e) || Date.now() - e.time < 3000);
    _documentEditors.set(k, editors);
  });
  $editors.next(_documentEditors);
});

const addEditor = (documentId: string, editor: Reader) => {
  const editors = _documentEditors.get(documentId) || [];
  const index = editors.findIndex((e) => e.identity_id === editor.identity_id);
  if (index !== -1) {
    // update the user time
    editors[index].time = Date.now();
    _documentEditors.set(documentId, editors);
    $editors.next(_documentEditors);
    return;
  }
  // debugger;
  editors.push(editor);
  _documentEditors.set(documentId, editors);
  $editors.next(_documentEditors);
};

export const DocumentEditors = () => {
  useEffect(() => {
    const user = getCurrentUser();
    if (!user) return;
    const sub = documentService.pubsub$.pipe<ReaderMsg>(filterAction("reader")).subscribe((msg) => {
      // console.log("[Document Pubsub] new reader", msg);
      addEditor(msg.data.id, { ...msg.data, time: Date.now() });
    });

    return () => {
      // console.log("[DocumentEditors] unsubscribing...");
      sub.unsubscribe();
    };
  });
  return null;
};

// listens for document editor messages and updates the list of editors
// TODO how? the backend is stateless, so we need to store the list of editors somewhere
// how do we know when to remove an editor from the list?
export const useDocumentEditors = (documentId: string) => {
  const [editors, setEditors] = useState<Reader[]>([]);
  const user = getCurrentUser();
  const baseUser = user
    ? ({ id: user.profile.sub, identity_id: user.profile.sub, full_name: user.profile.name } as Reader)
    : null;

  useEffect(() => {
    // the encounter service pubsub can also emit other data types
    const sub = $editors.subscribe((msg) => {
      // console.log("[Document Pubsub] updating editors", msg, documentId, msg.get(documentId));
      // get the readers and filter out ourselves, as we will enforce that we are always included in the list of editors
      const readers = (msg.get(documentId) || []).filter((e) => e.identity_id !== baseUser?.identity_id);
      setEditors([baseUser, ...readers].filter((e) => e !== null) as Reader[]);
    });
    return () => {
      // console.log("[Document Pubsub] unsubscribing...");
      sub.unsubscribe();
    };
  }, [setEditors]);

  return editors;
};

export const createContactIdentifier = contactsService.endpoint()
  .bodyOf<Pick<ContactIdentifierRecord, "namespace" | "key" | "value" | "contactId"> & { createIfMissing?: boolean; contact?: Record<string, unknown> }>()
  .responseOf<ContactIdentifierRecord>()
  .build({ id: "createContactIdentifier", path: "/custom/contactIdentifier", method: "post" });
export const updateContactIdentifier = contactsService.endpoint()
  .bodyOf<Pick<ContactIdentifierRecord, "id" | "contactId">>()
  .responseOf<ContactIdentifierRecord>()
  .build({ id: "updateContactIdentifier", path: "/custom/contactIdentifier", method: "patch" });
export const deleteContactIdentifier = contactsService.endpoint()
  .queryOf<{ id: string }>()
  .build({ id: "deleteContactIdentifier", path: "/custom/contactIdentifier", method: "delete" });
